import { requiresPriorConsent } from "./region";
import {
	CONSENT_COOKIE_NAME,
	CONSENT_VERSION,
	type ConsentRecord,
} from "./types";

/**
 * Server-side counterpart to `readConsent()` (which is browser-only). Parses
 * the `ld_consent` cookie out of a raw `Cookie:` header so route handlers can
 * honor an analytics opt-out before persisting first-party personalization
 * telemetry to D1.
 *
 * The cookie value is `encodeURIComponent(JSON.stringify(record))` — see
 * `writeConsent()` in `./storage.ts`.
 */
export function readConsentFromCookieHeader(
	cookieHeader: string | null | undefined,
): ConsentRecord | null {
	if (!cookieHeader) return null;
	const match = cookieHeader
		.split(/;\s*/)
		.find((c) => c.startsWith(`${CONSENT_COOKIE_NAME}=`));
	if (!match) return null;

	const raw = match.slice(CONSENT_COOKIE_NAME.length + 1);
	if (!raw) return null;

	try {
		const parsed = JSON.parse(decodeURIComponent(raw)) as ConsentRecord;
		if (
			!parsed ||
			typeof parsed !== "object" ||
			parsed.version !== CONSENT_VERSION ||
			!parsed.choices ||
			parsed.choices.necessary !== true
		) {
			return null;
		}
		return parsed;
	} catch {
		return null;
	}
}

/**
 * Tri-state analytics consent for a request:
 * - `true`  — user explicitly granted analytics.
 * - `false` — user explicitly rejected analytics (suppress profiling writes).
 * - `null`  — no valid decision recorded yet (legitimate-interest default).
 */
export function analyticsConsentFromCookieHeader(
	cookieHeader: string | null | undefined,
): boolean | null {
	const record = readConsentFromCookieHeader(cookieHeader);
	if (!record) return null;
	return record.choices.analytics === true;
}

/**
 * Whether a first-party personalization write to D1 must be suppressed for
 * this request.
 *
 * Encodes the consent model the product actually publishes (privacy policy,
 * "Cookies & Similar Technologies"): first-party product analytics runs on a
 * **legitimate-interest** basis, *except* in the EEA, UK and Switzerland,
 * where it is **prior opt-in**. That is two rules, not one:
 *
 * | region             | consent state        | write? |
 * |--------------------|----------------------|--------|
 * | anywhere           | explicit reject      | no     |
 * | prior-consent      | undecided            | no     |
 * | prior-consent      | explicit grant       | yes    |
 * | legitimate-interest| undecided            | yes    |
 * | legitimate-interest| explicit grant       | yes    |
 *
 * The "prior-consent + undecided" row is the one that a plain
 * `analyticsConsent === false` check misses: it lets a visitor who has not yet
 * answered the banner be profiled in a region where silence is not consent.
 * The client PostHog path already fails closed for exactly that visitor
 * (`posthog-provider.tsx` does not initialize until consent is granted in
 * these regions), so a server path that writes anyway makes the two disagree
 * about the same person.
 *
 * Fails closed on unknown geo, via `requiresPriorConsent`, matching the
 * client. On workerd `cf-ipcountry` is always present; off it (local dev,
 * unit tests) the strict branch applies, which is the safe direction.
 */
export function shouldSuppressPersonalizationWrite(input: {
	analyticsConsent: boolean | null;
	country: string | null | undefined;
}): boolean {
	const { analyticsConsent, country } = input;
	if (analyticsConsent === false) return true;
	if (analyticsConsent === true) return false;
	return requiresPriorConsent(country);
}
